import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Check, Loader2, Briefcase, Wrench, Building2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  maskCommessaCodice,
  COMMESSA_CODICE_RE,
  getCommessaFormat,
  commessaCodiceBaseRe,
  commessaEsempio,
} from "@/lib/commessaCodice";

/**
 * Combobox commessa che permette:
 *  - di selezionare da lista esistente (digitando 1+ lettera)
 *  - di creare una nuova commessa al volo con dialog (chiede codice, descrizione, cliente)
 *
 * Props:
 *  - commesse: array di {id, codice, descrizione, cliente}
 *  - value: codice attualmente selezionato (string)
 *  - onSelect({id, codice, descrizione}): chiamata su selezione/creazione
 *  - onCreated(commessa): chiamata DOPO la creazione di una nuova commessa
 *    (utile per aggiornare la lista globale)
 *  - testId
 */
export default function CommessaCombobox({
  commesse = [],
  value = "",
  onSelect,
  onCreated,
  testId = "commessa-combobox",
  compact = false,
  giornoData = "",
}) {
  const [text, setText] = useState(value || "");
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // Iter 31gg: true se l'utente ha navigato la lista con le frecce —
  // selezione intenzionale, non applichiamo il blocco sorella "-OFF".
  const [navigated, setNavigated] = useState(false);
  const wrapperRef = useRef(null);

  // Dialog "Crea nuova commessa"
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    codice: "",
    descrizione: "",
    cliente: "",
    tipo_struttura: "cantiere",
  });
  const [creating, setCreating] = useState(false);

  // Sync external value changes (e.g. when riga is loaded)
  useEffect(() => {
    setText(value || "");
  }, [value]);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const q = (text || "").trim();
  const qLow = q.toLowerCase();
  const filtered = q
    ? commesse
        .filter(
          (c) =>
            (c.codice || "").toLowerCase().includes(qLow) ||
            (c.descrizione || "").toLowerCase().includes(qLow)
        )
        .slice(0, 8)
    : commesse.slice(0, 8);

  const exactMatch = commesse.find(
    (c) => (c.codice || "").toLowerCase() === qLow
  );
  // Iter 34: la creazione di nuove commesse è riservata ad admin e utenti
  // con flag can_add_commesse (attivabile dall'admin in Utenti).
  const userCanCreate = user?.role === "admin" || !!user?.can_add_commesse;
  const requiresConfirmation = user?.role !== "admin";
  const canCreate = userCanCreate && q.length >= 2 && !exactMatch;

  // Iter 31gg: BLOCCO anti-sostituzione "-OFF". Se l'utente ha digitato per
  // intero un codice base NUOVO (es. EG-71-456) ma esiste solo la sorella
  // officina (EG-71-456-OFF), Tab/Enter NON devono sostituire il codice
  // digitato con quello -OFF. La sorella resta selezionabile con click o
  // navigando esplicitamente con le frecce.
  const isOffSiblingOfTyped = (c) =>
    !navigated &&
    !exactMatch &&
    (c?.codice || "").toUpperCase() === `${q.toUpperCase()}-OFF`;

  // Iter 67: commessa chiusa dal giorno del rapporto in poi → non selezionabile
  const isChiusa = (c) =>
    !!(c?.chiusa && c?.chiusa_dal && giornoData && giornoData >= c.chiusa_dal);
  const fmtChiusaDal = (iso) =>
    iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : "";

  const handleSelect = (c) => {
    if (isChiusa(c)) {
      toast.error(
        `Commessa ${c.codice} CHIUSA dal ${fmtChiusaDal(c.chiusa_dal)}: non si possono più inserire ore da quella data in poi`
      );
      return;
    }
    setText(c.codice);
    setOpen(false);
    onSelect?.({ id: c.id, codice: c.codice, descrizione: c.descrizione || "" });
  };

  const openCreateDialog = () => {
    // Iter 30m: se l'utente inizia a digitare un codice "vergine" senza
    // prefisso, pre-suggerisci il prefisso configurato (default "EG-").
    const P = getCommessaFormat().prefix;
    let initialCodice = q;
    if (initialCodice && !/^[A-Z]/.test(initialCodice)) {
      initialCodice = `${P}-${initialCodice}`;
    } else if (!initialCodice) {
      initialCodice = `${P}-`;
    }
    // Iter 30o: strip prefissi duplicati (paranoico — in caso il valore venisse
    // da un auto-fill seguito da typing manuale del prefisso).
    if (initialCodice.toUpperCase().startsWith(`${P}-${P}-`)) {
      initialCodice = `${P}-` + initialCodice.substring(P.length * 2 + 2);
    }
    setCreateForm({
      codice: initialCodice,
      descrizione: "",
      cliente: "",
      tipo_struttura: "cantiere",
    });
    setOpen(false);
    setCreateOpen(true);
  };

  // Iter 30o: detect commessa "sorella" (cantiere ↔ officina) per pre-fillare
  // automaticamente cliente e descrizione dai dati esistenti.
  // Esempio: creando EG-54-036-OFF, se EG-54-036 esiste, eredita i suoi dati.
  const sibling = (() => {
    const c = (createForm.codice || "").trim().toUpperCase();
    if (!c || c === `${getCommessaFormat().prefix}-`) return null;
    // Base = codice senza eventuale -OFF
    const base = c.endsWith("-OFF") ? c.slice(0, -4) : c;
    // Codice finale che il backend salverà
    const finalCode =
      createForm.tipo_struttura === "officina"
        ? `${base}-OFF`
        : base;
    // Codice della sorella (l'altro tipo)
    const siblingCode =
      createForm.tipo_struttura === "officina" ? base : `${base}-OFF`;
    if (!siblingCode || siblingCode === finalCode) return null;
    return (
      commesse.find((cm) => (cm.codice || "").toUpperCase() === siblingCode) ||
      null
    );
  })();

  // Auto-prefill cliente / descrizione dalla sorella quando l'utente non li
  // ha ancora compilati a mano (campi vuoti). Si attiva quando viene rilevata
  // una sorella o quando l'utente cambia tipo_struttura.
  useEffect(() => {
    if (!createOpen || !sibling) return;
    setCreateForm((prev) => {
      const patch = {};
      if (!(prev.descrizione || "").trim() && sibling.descrizione) {
        patch.descrizione = sibling.descrizione;
      }
      if (!(prev.cliente || "").trim() && sibling.cliente) {
        patch.cliente = sibling.cliente;
      }
      if (Object.keys(patch).length === 0) return prev;
      return { ...prev, ...patch };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sibling?.id, createOpen]);

  // Iter 34: suggerimento CLIENTE dalla "famiglia" EG-XX. Se il codice digitato
  // inizia con EG-XX e altre commesse della stessa famiglia hanno già un
  // cliente, lo suggeriamo (il prefisso 99 è jolly: nessun suggerimento).
  const familyCliente = (() => {
    const c = (createForm.codice || "").trim().toUpperCase();
    const m = c.match(/^EG-(\d{2})-/);
    if (!m || m[1] === "99") return null;
    const prefix = `EG-${m[1]}-`;
    const counts = {};
    for (const cm of commesse) {
      const code = (cm.codice || "").toUpperCase();
      const cli = (cm.cliente || "").trim();
      if (code.startsWith(prefix) && cli) {
        counts[cli] = (counts[cli] || 0) + 1;
      }
    }
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return best ? best[0] : null;
  })();

  // Auto-prefill del cliente suggerito dalla famiglia quando il campo è vuoto
  // (la sorella diretta, se esiste, ha comunque priorità — vedi effect sopra).
  useEffect(() => {
    if (!createOpen || !familyCliente) return;
    setCreateForm((prev) => {
      if ((prev.cliente || "").trim()) return prev;
      return { ...prev, cliente: familyCliente };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyCliente, createOpen]);

  const handleCreate = async () => {
    let codice = (createForm.codice || "").trim();
    if (!codice || codice === `${getCommessaFormat().prefix}-`) {
      toast.error("Inserisci il codice commessa completo");
      return;
    }
    // Officina: il backend forza il suffisso "-OFF". Lo rimuoviamo qui se l'utente
    // l'ha già scritto, per evitare doppio suffisso "EG-72-OFF-OFF".
    if ((createForm.tipo_struttura || "cantiere") === "officina") {
      codice = codice.replace(/-OFF$/i, "").trim();
    }
    // Iter 50/55: vincolo formato per TUTTI (admin inclusi) — secondo il
    // formato configurato. Il backend applica comunque lo stesso controllo.
    if (!commessaCodiceBaseRe().test(codice)) {
      toast.error(
        `Formato codice non valido: usare ${commessaEsempio()}. Il formato è obbligatorio per tutti.`
      );
      return;
    }
    // Iter 46: descrizione del lavoro obbligatoria per tutti
    if (!(createForm.descrizione || "").trim()) {
      toast.error(
        "Descrizione del lavoro obbligatoria: indica in breve di che lavoro si tratta."
      );
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/commesse", {
        codice,
        descrizione: (createForm.descrizione || "").trim(),
        cliente: (createForm.cliente || "").trim(),
        tipo_struttura: createForm.tipo_struttura || "cantiere",
        attiva: true,
      });
      if (data?.pending_confirmation) {
        toast.success(
          `Commessa ${data.codice} creata: in attesa di conferma dell'amministratore`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Commessa ${data.codice} creata`);
      }
      setText(data.codice);
      setCreateOpen(false);
      onCreated?.(data);
      onSelect?.({ id: data.id, codice: data.codice, descrizione: data.descrizione || "" });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Creazione non riuscita";
      toast.error(typeof msg === "string" ? msg : "Creazione non riuscita");
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    // Iter 31q: TAB completa con il suggerimento corrente E lascia che il
    // browser passi naturalmente al prossimo input (non chiamiamo
    // preventDefault). Funziona anche con menu chiuso se c'è UN solo match.
    if (e.key === "Tab" && !e.shiftKey) {
      const candidate = filtered[open ? highlight : 0];
      // Iter 31gg: se il candidato è la sorella -OFF del codice base digitato
      // per intero, NON completare: il codice digitato resta e il focus passa
      // alla cella successiva (l'utente sta creando la commessa base nuova).
      if (candidate && !isOffSiblingOfTyped(candidate) && !isChiusa(candidate)) {
        handleSelect(candidate);
        // Non preventDefault → il browser sposta il focus alla prossima cella
        return;
      }
      if (candidate) {
        // Sorella -OFF bloccata: chiudi il menu e lascia passare il Tab
        setOpen(false);
        return;
      }
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setNavigated(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setNavigated(true);
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cand = filtered[highlight];
      if (cand && !isOffSiblingOfTyped(cand)) handleSelect(cand);
      else if (canCreate) openCreateDialog();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        data-testid={testId}
        value={text}
        onChange={(e) => {
          let newVal = e.target.value;
          // Iter 30n: in modalità riga compatta (rapportino), quando l'utente
          // svuota completamente il campo per cambiare commessa, ripristina
          // automaticamente il prefisso configurato come predefinito.
          const _P = getCommessaFormat().prefix;
          if (compact && newVal === "" && text !== "") {
            newVal = `${_P}-`;
          }
          // Iter 30o: collassa duplicati (es. "LB-LB-") che possono nascere se
          // l'utente, dopo l'auto-fill del prefisso, lo digita di nuovo
          // per abitudine. Mantiene solo un prefisso.
          if (compact && newVal.toUpperCase().startsWith(`${_P}-${_P}-`)) {
            newVal = `${_P}-` + newVal.substring(_P.length * 2 + 2);
          }
          // Iter 50: trattini automatici — l'utente digita solo le cifre
          newVal = maskCommessaCodice(newVal, {
            deleting: newVal.length < (text || "").length,
          });
          setText(newVal);
          setOpen(true);
          setHighlight(0);
          setNavigated(false);
          // Clear binding if user is typing something new
          if (newVal !== value) {
            onSelect?.({ id: null, codice: newVal, descrizione: "" });
          }
        }}
        onFocus={(e) => {
          setOpen(true);
          // Iter 30n: se l'utente focalizza un campo vuoto in modalità riga
          // compatta, prefiglialo con la sigla configurata (es. "LB-").
          if (compact && !text) {
            const P = getCommessaFormat().prefix;
            setText(`${P}-`);
            onSelect?.({ id: null, codice: `${P}-`, descrizione: "" });
            // Sposta il cursore alla fine dopo il setState
            setTimeout(() => {
              try {
                const el = e.target;
                el.setSelectionRange(el.value.length, el.value.length);
              } catch (_) { /* noop */ }
            }, 0);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={compact ? "Codice…" : "Codice o descrizione…"}
        className={compact ? "h-9 text-sm" : "h-12 text-base"}
        autoComplete="off"
      />
      {open && (
        <div
          className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto"
          data-testid={`${testId}-popover`}
        >
          {filtered.length > 0 && (
            <ul>
              {filtered.map((c, idx) => {
                const chiusa = isChiusa(c);
                return (
                <li
                  key={c.id}
                  data-testid={`${testId}-item-${idx}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(c);
                  }}
                  className={`px-3 py-3 text-sm flex items-center gap-2 ${
                    chiusa
                      ? "opacity-60 cursor-not-allowed bg-slate-50"
                      : "cursor-pointer hover:bg-slate-100"
                  } ${idx === highlight && !chiusa ? "bg-slate-100" : ""}`}
                >
                  <Briefcase className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{c.codice}</div>
                    {c.descrizione && (
                      <div className="text-xs text-slate-500 truncate">
                        {c.descrizione}
                      </div>
                    )}
                  </div>
                  {chiusa && (
                    <span
                      data-testid={`${testId}-chiusa-badge-${idx}`}
                      title={`Chiusa dal ${fmtChiusaDal(c.chiusa_dal)}`}
                      className="shrink-0 text-[10px] font-black text-red-700 border border-red-200 bg-red-50 rounded px-1.5 py-0.5"
                    >
                      CHIUSA {fmtChiusaDal(c.chiusa_dal)}
                    </span>
                  )}
                  {c.codice === value && !chiusa && <Check className="h-4 w-4 text-[#0044FF]" />}
                </li>
                );
              })}
            </ul>
          )}
          {filtered.length === 0 && q && (
            <div className="px-3 py-3 text-sm text-slate-500">
              Nessuna commessa trovata.
            </div>
          )}
          {canCreate && (
            <div className="border-t border-slate-200 p-2 bg-slate-50">
              <Button
                type="button"
                onClick={openCreateDialog}
                data-testid={`${testId}-create-button`}
                className="w-full h-11 bg-[#0044FF] hover:bg-[#0033CC]"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crea nuova commessa &quot;{q}&quot;
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Dialog creazione commessa */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid={`${testId}-create-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-[#0044FF]" /> Nuova commessa
            </DialogTitle>
            <DialogDescription>
              Inserisci codice, descrizione e cliente. Puoi modificarli in seguito in
              Anagrafica → Commesse.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold">Tipo struttura</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() =>
                    setCreateForm({ ...createForm, tipo_struttura: "cantiere" })
                  }
                  data-testid={`${testId}-create-tipo-cantiere`}
                  className={`h-11 rounded-md border-2 flex items-center justify-center gap-2 text-sm font-bold transition-colors ${
                    (createForm.tipo_struttura || "cantiere") === "cantiere"
                      ? "border-[#0044FF] bg-blue-50 text-[#0044FF]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <Building2 className="h-4 w-4" /> Cantiere
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCreateForm({ ...createForm, tipo_struttura: "officina" })
                  }
                  data-testid={`${testId}-create-tipo-officina`}
                  className={`h-11 rounded-md border-2 flex items-center justify-center gap-2 text-sm font-bold transition-colors ${
                    createForm.tipo_struttura === "officina"
                      ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <Wrench className="h-4 w-4" /> Officina
                </button>
              </div>
              {createForm.tipo_struttura === "officina" && (
                <p className="text-[11px] text-amber-700 mt-1.5">
                  Il codice avrà automaticamente il suffisso{" "}
                  <span className="font-bold font-mono">-OFF</span>.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold">Codice *</Label>
              <Input
                value={createForm.codice}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    codice: maskCommessaCodice(e.target.value, {
                      deleting: e.target.value.length < (createForm.codice || "").length,
                    }),
                  })
                }
                placeholder="es. EG-72-087"
                className="h-11 mt-1"
                autoFocus
                data-testid={`${testId}-create-codice`}
              />
              {sibling && (
                <p
                  className="text-[11px] text-emerald-700 mt-1.5 flex items-center gap-1"
                  data-testid={`${testId}-sibling-hint`}
                >
                  <Check className="h-3 w-3" />
                  Commessa collegata trovata:{" "}
                  <span className="font-bold font-mono">{sibling.codice}</span>. Cliente e
                  descrizione ereditati automaticamente.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold">
                Descrizione <span className="text-red-500">*</span>
              </Label>
              <Input
                value={createForm.descrizione}
                onChange={(e) => setCreateForm({ ...createForm, descrizione: e.target.value })}
                placeholder="es. Manutenzione capannone (obbligatoria)"
                className="h-11 mt-1"
                data-testid={`${testId}-create-descrizione`}
              />
            </div>
            <div>
              <Label className="text-xs font-bold">Cliente</Label>
              <Input
                value={createForm.cliente}
                onChange={(e) => setCreateForm({ ...createForm, cliente: e.target.value })}
                placeholder="es. Rossi Srl"
                className="h-11 mt-1"
                data-testid={`${testId}-create-cliente`}
              />
              {familyCliente && (createForm.cliente || "").trim() === familyCliente && !sibling && (
                <p
                  className="text-[11px] text-emerald-700 mt-1.5 flex items-center gap-1"
                  data-testid={`${testId}-family-cliente-hint`}
                >
                  <Check className="h-3 w-3" />
                  Cliente suggerito dal codice{" "}
                  <span className="font-bold font-mono">
                    EG-{(createForm.codice.match(/^EG-(\d{2})-/i) || [])[1] || "XX"}
                  </span>: già usato dalle altre commesse di questa famiglia.
                </p>
              )}
              {!familyCliente && !sibling && (
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Nessun cliente trovato per questo codice: inseriscilo manualmente.
                </p>
              )}
            </div>
            {requiresConfirmation && (
              <div
                className="rounded-md border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900"
                data-testid={`${testId}-pending-note`}
              >
                La commessa sarà utilizzabile subito nel rapportino, ma resterà
                <span className="font-bold"> "da confermare"</span>: l'amministratore
                riceverà un avviso al suo prossimo accesso.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
              data-testid={`${testId}-create-cancel`}
            >
              Annulla
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !createForm.codice.trim()}
              className="bg-[#0044FF] hover:bg-[#0033CC]"
              data-testid={`${testId}-create-confirm`}
            >
              {creating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Crea commessa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
